import { FormEvent, useEffect, useState } from "react";
import { api, Session } from "../api";
import { BrandLogo } from "../components/BrandLogo";
import { workspaceDomain } from "../lib/workspace";

export function Invitation({ onAccepted }: { onAccepted: (session: Session) => void }) {
  // Fragment stays out of HTTP access logs and referrers; never persist the invitation token.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");
  const [details, setDetails] = useState<Awaited<ReturnType<typeof api.invitationPreview>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const domain = workspaceDomain();
  const loginUrl = `/${window.location.search}`;

  useEffect(() => {
    let cancelled = false;
    api.invitationPreview(token, domain).then((result) => { if (!cancelled) setDetails(result); })
      .catch((reason: unknown) => { if (!cancelled) setError(reason instanceof Error ? reason.message : "초대를 확인하지 못했습니다."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, domain]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!details || busy) return;
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password"));
    if (!details.existingAccount && password !== form.get("confirmPassword")) {
      setError("비밀번호 확인이 일치하지 않습니다."); return;
    }
    if (new TextEncoder().encode(password).length > 72) {
      setError("비밀번호는 UTF-8 기준 72바이트 이내로 입력해 주세요."); return;
    }
    setBusy(true); setError("");
    try {
      const result = await api.acceptInvitation(token, domain, password);
      setAccepted(true);
      window.history.replaceState(null, "", loginUrl);
      const workspace = await api.workspaceContext(domain);
      const session = await api.login(workspace, result.email, password);
      onAccepted(session);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "초대를 수락하지 못했습니다.");
    } finally { setBusy(false); }
  }

  return <main className="login-page"><section className="login-panel"><form onSubmit={submit}>
    <div className="auth-brand"><BrandLogo /></div>
    {loading ? <div className="workspace-context-state" role="status"><span className="context-spinner" /><p>초대를 확인하고 있습니다.</p></div>
      : accepted ? <><h1>초대 수락 완료</h1><p>회사에 참여했습니다. 로그인을 완료해 주세요.</p>{error && <div className="error-box" role="alert">{error}</div>}<a className="primary auth-home-link" href={loginUrl}>로그인으로 이동</a></>
      : !details ? <><h1>초대를 사용할 수 없습니다</h1><div className="error-box" role="alert">{error}</div><p>조직 관리자에게 초대 메일을 다시 보내 달라고 요청해 주세요.</p><a className="secondary auth-home-link" href={loginUrl}>로그인으로 이동</a></>
      : <>
        <header className="auth-heading"><h1>{details.companyName}에 초대되었습니다</h1>
          <p>{details.inviterName}님이 초대했습니다. {details.existingAccount ? "기존 계정으로 로그인하고 초대를 수락하세요." : "사용할 비밀번호를 설정하고 시작하세요."}</p></header>
        <label>이메일<input type="email" value={details.email} autoComplete="username" readOnly /></label>
        <label>{details.existingAccount ? "비밀번호" : "새 비밀번호"}<input name="password" type="password" autoComplete={details.existingAccount ? "current-password" : "new-password"}
          minLength={details.existingAccount ? undefined : 10} maxLength={72} required autoFocus /></label>
        {!details.existingAccount && <><small>비밀번호는 10자 이상, UTF-8 기준 72바이트 이내로 입력하세요.</small>
          <label>새 비밀번호 확인<input name="confirmPassword" type="password" autoComplete="new-password" minLength={10} maxLength={72} required /></label></>}
        {error && <div className="error-box" role="alert">{error}</div>}
        <button className="primary wide" disabled={busy}>{busy ? "처리 중…" : details.existingAccount ? "로그인하고 초대 수락" : "비밀번호 설정하고 시작"}</button>
      </>}
  </form></section></main>;
}
